import React from 'react';
import {
  Stack,
  Select,
  TextInput,
  NumberInput,
  Switch,
  Text,
  Divider,
  TagsInput
} from '@mantine/core';
import { OptionsEditor } from './OptionsEditor';

const INPUT_TYPES = [
  { value: 'formula', label: 'Formula' },
  { value: 'boolean', label: 'Boolean' },
  { value: 'string', label: 'String' },
  { value: 'number', label: 'Number' },
  { value: 'select', label: 'Select' },
  { value: 'multiString', label: 'Multi String' },
  { value: 'multiNumber', label: 'Multi Number' },
  { value: 'multiSelect', label: 'Multi Select' }
] as const;

type InputType = typeof INPUT_TYPES[number]['value'];

type InputConfigEntry = {
  type: InputType;
  label?: string;
  description?: string;
  isRequired?: boolean;
  options?: Record<string, unknown>;
  defaultValue?: string | string[] | boolean | number | number[];
  inputName: string;
};

export type InputsConfigEditorProps = {
  inputsConfig: Record<string, unknown>;
  onChange: ( inputsConfig: Record<string, unknown> ) => void;
};

const INPUT_STYLES = {
  label: { color: 'var(--drawer-label)', fontSize: 12, fontWeight: 600 },
  input: {
    background: 'var(--drawer-input-bg)',
    borderColor: 'var(--drawer-input-border)',
    color: 'var(--drawer-text)'
  }
} as const;

/**
 * Renders a section for each inputsConfig entry with type selector,
 * label, description, isRequired, default value, and options for select types.
 */
export function InputsConfigEditor({ inputsConfig, onChange }: InputsConfigEditorProps ) {
  const entries = Object.entries( inputsConfig ) as [ string, InputConfigEntry ][];

  if ( entries.length === 0 ) {
    return (
      <div style={{ padding: '16px 0', textAlign: 'center' }}>
        <span style={{ color: 'var(--drawer-dimmed)', fontSize: 12 }}>
          No inputs configured for this node.
        </span>
      </div>
    );
  }

  const handleEntryChange = ( key: string, updated: Partial<InputConfigEntry> ) => {
    const current = inputsConfig[key] as InputConfigEntry;
    onChange({
      ...inputsConfig,
      [key]: { ...current, ...updated }
    });
  };

  return (
    <Stack gap="md">
      {entries.map(([ key, entry ], index ) => (
        <React.Fragment key={key}>
          {index > 0 && <Divider color="var(--drawer-border)" />}
          <SingleInputConfigEditor
            inputKey={key}
            entry={entry}
            onChange={( updated ) => handleEntryChange( key, updated )}
          />
        </React.Fragment>
      ))}
    </Stack>
  );
}

type SingleInputConfigEditorProps = {
  inputKey: string;
  entry: InputConfigEntry;
  onChange: ( updated: Partial<InputConfigEntry> ) => void;
};

function SingleInputConfigEditor({ inputKey, entry, onChange }: SingleInputConfigEditorProps ) {
  const isSelectType = entry.type === 'select' || entry.type === 'multiSelect';

  return (
    <Stack gap="xs">
      <Text size="xs" fw={700} c="var(--drawer-text)">
        {inputKey}
      </Text>

      <Select
        label="Type"
        value={entry.type}
        onChange={( val ) => {
          if ( val ) {
            const updates: Partial<InputConfigEntry> = { type: val as InputType };
            // Reset default value when type changes
            if ( val === 'boolean' ) {
              updates.defaultValue = false;
            } else if ( val === 'number' ) {
              updates.defaultValue = 0;
            } else if ( val === 'multiString' || val === 'multiNumber' || val === 'multiSelect' ) {
              updates.defaultValue = [];
            } else {
              updates.defaultValue = '';
            }
            // Clear options if moving away from select types
            if ( val !== 'select' && val !== 'multiSelect' ) {
              updates.options = undefined;
            }
            onChange( updates );
          }
        }}
        data={INPUT_TYPES as unknown as { value: string; label: string }[]}
        size="xs"
        styles={INPUT_STYLES}
      />

      <TextInput
        label="Label"
        value={entry.label ?? ''}
        onChange={( e ) => onChange({ label: e.currentTarget.value })}
        size="xs"
        styles={INPUT_STYLES}
      />

      <TextInput
        label="Description"
        value={entry.description ?? ''}
        onChange={( e ) => onChange({ description: e.currentTarget.value })}
        size="xs"
        styles={INPUT_STYLES}
      />

      <Switch
        label="Required"
        checked={entry.isRequired ?? false}
        onChange={( e ) => onChange({ isRequired: e.currentTarget.checked })}
        size="xs"
        styles={{
          label: { color: 'var(--drawer-label)', fontSize: 12, fontWeight: 600 }
        }}
      />

      <DefaultValueEditor
        type={entry.type}
        value={entry.defaultValue}
        onChange={( val ) => onChange({ defaultValue: val })}
      />

      {isSelectType && (
        <>
          <Text size="xs" fw={600} c="dimmed" tt="uppercase" style={{ letterSpacing: '0.5px', marginTop: 8 }}>
            Options
          </Text>
          <OptionsEditor
            options={( entry.options ?? {}) as Record<string, Record<string, unknown>>}
            onChange={( opts ) => onChange({ options: opts })}
          />
        </>
      )}
    </Stack>
  );
}

type DefaultValueEditorProps = {
  type: InputType;
  value: string | string[] | boolean | number | number[] | undefined;
  onChange: ( value: string | string[] | boolean | number | number[]) => void;
};

function DefaultValueEditor({ type, value, onChange }: DefaultValueEditorProps ) {
  switch ( type ) {
    case 'boolean':
      return (
        <Switch
          label="Default Value"
          checked={typeof value === 'boolean' ? value : false}
          onChange={( e ) => onChange( e.currentTarget.checked )}
          size="xs"
          styles={{
            label: { color: 'var(--drawer-label)', fontSize: 12, fontWeight: 600 }
          }}
        />
      );

    case 'number':
      return (
        <NumberInput
          label="Default Value"
          value={typeof value === 'number' ? value : 0}
          onChange={( val ) => onChange( typeof val === 'number' ? val : 0 )}
          size="xs"
          styles={INPUT_STYLES}
        />
      );

    case 'multiString':
    case 'multiSelect':
      return (
        <TagsInput
          label="Default Values"
          value={Array.isArray( value ) ? value.map( String ) : []}
          onChange={( vals ) => onChange( vals )}
          size="xs"
          styles={INPUT_STYLES}
          placeholder="Type and press Enter"
        />
      );

    case 'multiNumber':
      return (
        <TagsInput
          label="Default Values"
          value={Array.isArray( value ) ? value.map( String ) : []}
          onChange={( vals ) => onChange( vals.map( Number ).filter(( n ) => !isNaN( n )))}
          size="xs"
          styles={INPUT_STYLES}
          placeholder="Type numbers and press Enter"
        />
      );

    case 'formula':
    case 'string':
    case 'select':
    default:
      return (
        <TextInput
          label="Default Value"
          value={typeof value === 'string' ? value : String( value ?? '' )}
          onChange={( e ) => onChange( e.currentTarget.value )}
          size="xs"
          styles={INPUT_STYLES}
        />
      );
  }
}
